import React, { useState } from 'react';
import { 
  ExclamationTriangleIcon, 
  InformationCircleIcon, 
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/20/solid';
import { useTheme } from '../../context/ThemeContext';
import { ErrorSeverity, RecoveryActionType } from '../../utils/uploadErrorHandling';

/**
 * Reusable component for displaying validation errors and warnings
 * with recovery suggestions and expandable details
 */
const ErrorDisplay = ({
  errors = [],
  warnings = [],
  className = '',
  showRecoveryActions = true,
  collapsible = false,
  maxVisibleErrors = 3
}) => {
  const { isDarkMode } = useTheme();
  const [isExpanded, setIsExpanded] = useState(!collapsible);

  if (errors.length === 0 && warnings.length === 0) {
    return null;
  }

  const allIssues = [...errors, ...warnings];
  const visibleIssues = isExpanded ? allIssues : allIssues.slice(0, maxVisibleErrors);
  const hasMoreIssues = allIssues.length > maxVisibleErrors;

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case ErrorSeverity.ERROR:
        return {
          container: isDarkMode 
            ? 'bg-red-900/20 border-red-800/50' 
            : 'bg-red-50 border-red-200',
          icon: isDarkMode ? 'text-red-400' : 'text-red-500',
          text: isDarkMode ? 'text-red-400' : 'text-red-600',
          advice: isDarkMode ? 'text-red-300' : 'text-red-500'
        };
      case ErrorSeverity.WARNING:
        return {
          container: isDarkMode 
            ? 'bg-yellow-900/20 border-yellow-800/50' 
            : 'bg-yellow-50 border-yellow-200',
          icon: isDarkMode ? 'text-yellow-400' : 'text-yellow-600',
          text: isDarkMode ? 'text-yellow-400' : 'text-yellow-700',
          advice: isDarkMode ? 'text-yellow-300' : 'text-yellow-600'
        };
      case ErrorSeverity.INFO:
        return {
          container: isDarkMode 
            ? 'bg-blue-900/20 border-blue-800/50' 
            : 'bg-blue-50 border-blue-200',
          icon: isDarkMode ? 'text-blue-400' : 'text-blue-600',
          text: isDarkMode ? 'text-blue-400' : 'text-blue-700',
          advice: isDarkMode ? 'text-blue-300' : 'text-blue-600'
        };
      default:
        return {
          container: isDarkMode 
            ? 'bg-gray-900/20 border-gray-800/50' 
            : 'bg-gray-50 border-gray-200',
          icon: isDarkMode ? 'text-gray-400' : 'text-gray-500',
          text: isDarkMode ? 'text-gray-400' : 'text-gray-600',
          advice: isDarkMode ? 'text-gray-300' : 'text-gray-500'
        };
    }
  };

  const getIcon = (severity) => {
    switch (severity) {
      case ErrorSeverity.ERROR:
        return ExclamationTriangleIcon;
      case ErrorSeverity.WARNING:
      case ErrorSeverity.INFO:
      default:
        return InformationCircleIcon;
    }
  };

  const getRecoveryActionIcon = (actionType) => {
    // You could add specific icons for different action types
    return '→';
  };

  // Group issues by severity for better organization
  const errorIssues = visibleIssues.filter(issue => issue.severity === ErrorSeverity.ERROR);
  const warningIssues = visibleIssues.filter(issue => issue.severity === ErrorSeverity.WARNING);
  const infoIssues = visibleIssues.filter(issue => issue.severity === ErrorSeverity.INFO);

  const renderIssueGroup = (issues, severity) => {
    if (issues.length === 0) return null;

    const styles = getSeverityStyles(severity);
    const IconComponent = getIcon(severity);

    return (
      <div className={`p-4 rounded-lg border ${styles.container} mb-3 last:mb-0`}>
        <div className="flex items-start">
          <IconComponent className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${styles.icon}`} />
          <div className="flex-1 min-w-0">
            {issues.map((issue, index) => (
              <div key={`${issue.code}-${index}`} className="mb-3 last:mb-0">
                <p className={`text-sm font-medium ${styles.text}`}>
                  {issue.userFriendlyMessage || issue.message}
                </p>
                
                {issue.actionableAdvice && (
                  <p className={`text-xs mt-1 ${styles.advice}`}>
                    {issue.actionableAdvice}
                  </p>
                )}

                {/* Technical details for debugging */}
                {issue.technicalDetails && process.env.NODE_ENV === 'development' && (
                  <details className="mt-2">
                    <summary className={`text-xs cursor-pointer ${styles.advice} opacity-75`}>
                      Technical details
                    </summary>
                    <div className={`text-xs mt-1 ${styles.advice} opacity-75 font-mono`}>
                      Current: {JSON.stringify(issue.technicalDetails.currentValue)}<br/>
                      Expected: {JSON.stringify(issue.technicalDetails.expectedValue)}
                    </div>
                  </details>
                )}

                {/* Recovery actions */}
                {showRecoveryActions && issue.recoveryActions && issue.recoveryActions.length > 0 && (
                  <div className="mt-2">
                    <p className={`text-xs font-medium ${styles.text} mb-1`}>
                      What you can do:
                    </p>
                    <ul className="space-y-1">
                      {issue.recoveryActions.map((action, actionIndex) => (
                        <li key={`${action.type}-${actionIndex}`} className={`text-xs ${styles.advice} flex items-center`}>
                          <span className="mr-2 opacity-75">
                            {getRecoveryActionIcon(action.type)}
                          </span>
                          {action.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`space-y-0 ${className}`} role="alert" aria-live="polite">
      {renderIssueGroup(errorIssues, ErrorSeverity.ERROR)}
      {renderIssueGroup(warningIssues, ErrorSeverity.WARNING)}
      {renderIssueGroup(infoIssues, ErrorSeverity.INFO)}

      {/* Expand/Collapse button */}
      {collapsible && hasMoreIssues && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`
            w-full mt-2 px-3 py-2 text-xs font-medium rounded-md transition-colors duration-200
            ${isDarkMode 
              ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800 border border-gray-700' 
              : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50 border border-gray-200'
            }
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50
          `}
          aria-expanded={isExpanded}
          aria-controls="error-details"
        >
          <div className="flex items-center justify-center">
            {isExpanded ? (
              <>
                <ChevronUpIcon className="w-4 h-4 mr-1" />
                Show fewer issues
              </>
            ) : (
              <>
                <ChevronDownIcon className="w-4 h-4 mr-1" />
                Show {allIssues.length - maxVisibleErrors} more issue{allIssues.length - maxVisibleErrors > 1 ? 's' : ''}
              </>
            )}
          </div>
        </button>
      )}
    </div>
  );
};

export default ErrorDisplay;